import React, { useState, useEffect } from "react";
import Modal from "../Modal/Modal";
import {
  IFederation,
  IFederationCreateRequest,
} from "../../interfaces/federation.interface";
import FederationService from "../../services/FederationService";
import { useLoader } from "../../contexts/LoaderContext";
import { showToast } from "../Toaster";

interface FederationModalProps {
  isOpen: boolean;
  onClose: () => void;
  federation?: IFederation | null;
  onSuccess: () => void;
}

const FederationModal: React.FC<FederationModalProps> = ({
  isOpen,
  onClose,
  federation,
  onSuccess,
}) => {
  const { showLoader, hideLoader } = useLoader();
  const [formData, setFormData] = useState<IFederationCreateRequest>({
    name: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = !!federation;

  useEffect(() => {
    if (federation) {
      setFormData({
        name: federation.name,
        phoneNumber: federation.phoneNumber || "",
        email: federation.email || "",
        address: federation.address || "",
        gstNumber: federation.gstNumber || "",
        // Support legacy contact field - prioritize phoneNumber over legacy contact
        contact: federation.phoneNumber || federation.contact || "",
      });
    } else {
      setFormData({
        name: "",
        phoneNumber: "",
        email: "",
        address: "",
        gstNumber: "",
        contact: "",
      });
    }
    setErrors({});
  }, [federation]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Required field validation
    if (!formData.name.trim()) {
      newErrors.name = "Federation name is required";
    }

    // Email validation (only if email is provided)
    if (formData.email && formData.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = "Invalid email format";
      }
    }

    // Phone Number validation (optional but if provided, should be valid)
    if (formData.phoneNumber && formData.phoneNumber.trim()) {
      const phoneRegex = /^[+]?[\d\s\-()]{10,}$/;
      if (!phoneRegex.test(formData.phoneNumber)) {
        newErrors.phoneNumber =
          "Please enter a valid phone number (minimum 10 digits)";
      }
    }

    // GST Number validation (optional but if provided, should be valid)
    if (formData.gstNumber && formData.gstNumber.trim()) {
      const gstRegex =
        /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/;
      if (!gstRegex.test(formData.gstNumber.toUpperCase())) {
        newErrors.gstNumber = "Please enter a valid GST number (15 digits)";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async () => {
    const isValid = validateForm();

    if (!isValid) {
      return;
    }

    setIsSubmitting(true);
    showLoader();

    try {
      // Prepare the data for API submission
      const submitData = {
        name: formData.name.trim(),
        phoneNumber: formData.phoneNumber?.trim() || undefined,
        email: formData.email?.trim() || undefined,
        address: formData.address?.trim() || undefined,
        gstNumber: formData.gstNumber?.trim()?.toUpperCase() || undefined,
      };

      // Remove undefined fields
      Object.keys(submitData).forEach(
        (key) =>
          submitData[key as keyof typeof submitData] === undefined &&
          delete submitData[key as keyof typeof submitData]
      );

      if (isEditMode && federation) {
        await FederationService.updateFederation(federation.id, {
          id: federation.id,
          ...submitData,
        });
        showToast("Federation updated successfully", "success");
      } else {
        await FederationService.createFederation(submitData);
        showToast("Federation created successfully", "success");
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error("Error saving federation:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Error saving federation";
      showToast(errorMessage, "error");
    } finally {
      setIsSubmitting(false);
      hideLoader();
    }
  };

  const getFooterButtons = () => (
    <div className="flex justify-center gap-4">
      <button
        type="button"
        className="secondary_button w-[128px]"
        onClick={onClose}
        disabled={isSubmitting}
      >
        Cancel
      </button>
      <button
        type="button"
        className="primary_button w-[128px]"
        onClick={handleSubmit}
        disabled={isSubmitting}
      >
        {isSubmitting ? "Saving..." : isEditMode ? "Update" : "Create"}
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? "Edit Federation" : "Add Federation"}
      size="xl"
      closeOnModalOverlayClick={true}
      footer={getFooterButtons()}
    >
      <div className="modal-form">
        {/* 
          Updated Enterprise Federation fields:
          - Name (required)
          - Phone Number (optional)
          - Email (optional)
          - GST Number (optional)
          - Address (optional)
        */}
        {/* Read-only Federation Information (Edit Mode) */}
        {isEditMode && federation && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
            <h4 className="text-sm font-medium text-gray-700 mb-3">
              Federation Information
            </h4>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-600">
                  Identifier ID:
                </span>
                <div className="text-gray-800">{federation.identifierId}</div>
              </div>
              <div>
                <span className="font-medium text-gray-600">Clients:</span>
                <div className="text-gray-800">
                  {federation.clientList?.length || 0}
                </div>
              </div>
              <div>
                <span className="font-medium text-gray-600">Users:</span>
                <div className="text-gray-800">
                  {federation.users?.length || 0}
                </div>
              </div>
              {federation.createdBy && (
                <div>
                  <span className="font-medium text-gray-600">Created By:</span>
                  <div className="text-gray-800">
                    {federation.createdBy.name}
                  </div>
                </div>
              )}
              {federation.createDate && (
                <div>
                  <span className="font-medium text-gray-600">
                    Created Date:
                  </span>
                  <div className="text-gray-800">
                    {new Date(federation.createDate).toLocaleDateString()}
                  </div>
                </div>
              )}
              {federation.updateDate && (
                <div>
                  <span className="font-medium text-gray-600">
                    Last Updated:
                  </span>
                  <div className="text-gray-800">
                    {new Date(federation.updateDate).toLocaleDateString()}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Federation Name */}
          <div className="form-group lg:col-span-2">
            <label className="form-label required_field_label">
              Federation Name
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter federation name"
              className={`form-input ${errors.name ? "error" : ""}`}
            />
            {errors.name && (
              <div className="form-help error">{errors.name}</div>
            )}
          </div>

          {/* Phone Number */}
          <div className="form-group">
            <label className="form-label field_label">Phone Number</label>
            <input
              type="tel"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleInputChange}
              placeholder="Enter phone number"
              className={`form-input ${errors.phoneNumber ? "error" : ""}`}
            />
            {errors.phoneNumber && (
              <div className="form-help error">{errors.phoneNumber}</div>
            )}
          </div>

          {/* Email */}
          <div className="form-group">
            <label className="form-label field_label">Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Enter email address"
              className={`form-input ${errors.email ? "error" : ""}`}
            />
            {errors.email && (
              <div className="form-help error">{errors.email}</div>
            )}
          </div>

          {/* GST Number */}
          <div className="form-group">
            <label className="form-label field_label">GST Number</label>
            <input
              type="text"
              name="gstNumber"
              value={formData.gstNumber}
              onChange={handleInputChange}
              placeholder="Enter GST number (optional)"
              className={`form-input ${errors.gstNumber ? "error" : ""}`}
            />
            {errors.gstNumber && (
              <div className="form-help error">{errors.gstNumber}</div>
            )}
          </div>

          {/* Address */}
          <div className="form-group lg:col-span-2">
            <label className="form-label field_label">Address</label>
            <textarea
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              placeholder="Enter address"
              rows={3}
              className={`form-input ${errors.address ? "error" : ""}`}
            />
            {errors.address && (
              <div className="form-help error">{errors.address}</div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default FederationModal;
